// src/pages/AdminEmpresaInfo.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Grid,
  CircularProgress,
  Divider,
  Button,
  Stack,
  Chip,
  Paper,
  alpha,
  useTheme,
} from '@mui/material';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';

import { db } from '../contexts/firebase/config';
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getCountFromServer,
} from 'firebase/firestore';

function safe(str) {
  return str ?? '—';
}

function initials(name) {
  if (!name) return 'E';
  const parts = String(name).trim().split(' ');
  const [a, b] = [parts[0], parts[1]];
  return (a?.[0] || 'E').toUpperCase() + (b ? (b[0] || '').toUpperCase() : '');
}

export default function AdminEmpresaInfo() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { empresaId } = useParams();

  const colors = {
    accent: theme.palette.primary.main,
    surface: isDark ? 'rgba(15,23,42,0.78)' : 'rgba(255,255,255,0.78)',
    card: isDark ? 'rgba(15,23,42,0.84)' : 'rgba(255,255,255,0.84)',
    cardStrong: isDark ? 'rgba(15,23,42,0.96)' : 'rgba(255,255,255,0.96)',
    border: isDark ? 'rgba(148,163,184,0.16)' : 'rgba(15,23,42,0.08)',
    text: theme.palette.text.primary,
    muted: theme.palette.text.secondary,
    hero: isDark ? 'rgba(2,6,23,0.96)' : 'rgba(15,23,42,0.96)',
  };

  const [empresa, setEmpresa] = useState(null);
  const [stats, setStats] = useState({ clients: 0, purchases: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    async function fetchData() {
      setLoading(true);
      try {
        const ref = doc(db, 'empresas', empresaId);
        const snap = await getDoc(ref);
        const data = snap.exists() ? snap.data() : null;

        const qClients = query(
          collection(db, 'clientes'),
          where('empresaId', '==', empresaId)
        );

        const qPurch = query(
          collection(db, 'compras'),
          where('empresaId', '==', empresaId)
        );

        const [cCountSnap, pCountSnap] = await Promise.all([
          getCountFromServer(qClients),
          getCountFromServer(qPurch),
        ]);

        if (!active) return;

        setEmpresa(data);
        setStats({
          clients: cCountSnap.data().count,
          purchases: pCountSnap.data().count,
        });
      } catch {
        if (active) {
          setEmpresa(null);
          setStats({ clients: 0, purchases: 0 });
        }
      } finally {
        if (active) setLoading(false);
      }
    }

    fetchData();

    return () => {
      active = false;
    };
  }, [empresaId]);

  const avatarLabel = useMemo(() => initials(empresa?.nomeEmpresa), [empresa]);

  const InfoItem = ({ icon, label, value }) => (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        height: '100%',
        borderRadius: '24px',
        border: `1px solid ${colors.border}`,
        bgcolor: colors.cardStrong,
      }}
    >
      <Stack direction="row" spacing={1.35} alignItems="center">
        <Avatar
          sx={{
            width: 44,
            height: 44,
            borderRadius: '16px',
            bgcolor: alpha(colors.accent, isDark ? 0.18 : 0.1),
            color: colors.accent,
          }}
        >
          {icon}
        </Avatar>

        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: 800 }}
          >
            {label}
          </Typography>

          <Typography sx={{ fontWeight: 950, color: colors.text }} noWrap>
            {safe(value)}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );

  const KpiCard = ({ to, icon, title, value, description, color = colors.accent }) => (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: '30px',
        border: `1px solid ${colors.border}`,
        bgcolor: colors.card,
        overflow: 'hidden',
        boxShadow: isDark
          ? '0 24px 64px rgba(0,0,0,0.28)'
          : '0 24px 60px rgba(15,23,42,0.07)',
        transition: 'all .22s ease',
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: isDark
            ? '0 28px 74px rgba(0,0,0,0.36)'
            : '0 28px 70px rgba(15,23,42,0.11)',
        },
      }}
    >
      <CardContent sx={{ p: { xs: 2.4, md: 3 } }}>
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={2}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 900 }}
            >
              {title}
            </Typography>

            <Typography
              sx={{
                mt: 1.2,
                fontSize: { xs: '2.1rem', md: '2.65rem' },
                fontWeight: 950,
                lineHeight: 1,
                letterSpacing: '-0.06em',
                color: colors.text,
              }}
            >
              {value}
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 1, lineHeight: 1.7 }}
            >
              {description}
            </Typography>
          </Box>

          <Avatar
            sx={{
              width: 62,
              height: 62,
              borderRadius: '22px',
              bgcolor: alpha(color, isDark ? 0.2 : 0.1),
              color,
              flexShrink: 0,
            }}
          >
            {icon}
          </Avatar>
        </Stack>
      </CardContent>

      <Divider sx={{ borderColor: colors.border }} />

      <Box sx={{ p: 1.6, textAlign: 'right' }}>
        <Button
          component={RouterLink}
          to={to}
          size="small"
          variant="contained"
          endIcon={<ArrowForwardRoundedIcon />}
          sx={{
            borderRadius: '999px',
            px: 2,
            textTransform: 'none',
            fontWeight: 950,
            bgcolor: color,
            boxShadow: `0 14px 28px ${alpha(color, 0.24)}`,
            '&:hover': { bgcolor: color, filter: 'brightness(0.95)' },
          }}
        >
          Ver detalhes
        </Button>
      </Box>
    </Card>
  );

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          background: isDark
            ? 'linear-gradient(135deg, #020617 0%, #0f172a 52%, #111827 100%)'
            : 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 48%, #f8fafc 100%)',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!empresa) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          px: 2,
          background: isDark
            ? 'linear-gradient(135deg, #020617 0%, #0f172a 52%, #111827 100%)'
            : 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 48%, #f8fafc 100%)',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            maxWidth: 520,
            width: '100%',
            textAlign: 'center',
            borderRadius: '30px',
            border: `1px solid ${colors.border}`,
            bgcolor: colors.card,
          }}
        >
          <Avatar
            sx={{
              width: 72,
              height: 72,
              mx: 'auto',
              mb: 2,
              borderRadius: '26px',
              bgcolor: alpha(colors.accent, isDark ? 0.18 : 0.1),
              color: colors.accent,
            }}
          >
            <BusinessIcon />
          </Avatar>

          <Typography variant="h6" sx={{ fontWeight: 950 }}>
            Empresa não encontrada.
          </Typography>

          <Button
            component={RouterLink}
            to="/admin/parceiros"
            variant="contained"
            sx={{
              mt: 2,
              borderRadius: '999px',
              textTransform: 'none',
              fontWeight: 900,
            }}
          >
            Voltar à lista de parceiros
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
        px: { xs: 1.25, sm: 2.5, md: 3.5 },
        py: { xs: 2, md: 3.5 },
        background: isDark
          ? 'linear-gradient(135deg, #020617 0%, #0f172a 52%, #111827 100%)'
          : 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 48%, #f8fafc 100%)',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background: isDark
            ? `radial-gradient(900px 460px at 10% 5%, ${alpha(colors.accent, 0.18)}, transparent 62%), radial-gradient(760px 440px at 92% 12%, ${alpha(theme.palette.success.main, 0.12)}, transparent 58%)`
            : `radial-gradient(900px 460px at 10% 5%, ${alpha(colors.accent, 0.16)}, transparent 62%), radial-gradient(760px 440px at 92% 12%, ${alpha(theme.palette.success.main, 0.12)}, transparent 58%)`,
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: isDark ? 0.16 : 0.3,
          pointerEvents: 'none',
          backgroundImage:
            'linear-gradient(rgba(148,163,184,0.16) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.16) 1px, transparent 1px)',
          backgroundSize: '44px 44px',
          maskImage: 'linear-gradient(to bottom, black, transparent 76%)',
        }}
      />

      <Box sx={{ position: 'relative', zIndex: 1, width: '100%' }}>
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            borderRadius: { xs: '28px', md: '42px' },
            border: `1px solid ${colors.border}`,
            bgcolor: colors.surface,
            backdropFilter: 'blur(22px)',
            WebkitBackdropFilter: 'blur(22px)',
            boxShadow: isDark
              ? '0 28px 90px rgba(0,0,0,0.36)'
              : '0 24px 80px rgba(15,23,42,0.10)',
            p: { xs: 2, sm: 3, md: 4 },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              position: 'relative',
              overflow: 'hidden',
              width: '100%',
              mb: 3,
              p: { xs: 2.5, md: 4 },
              borderRadius: '34px',
              bgcolor: colors.hero,
              color: 'common.white',
              boxShadow: isDark
                ? '0 24px 70px rgba(0,0,0,0.34)'
                : '0 24px 70px rgba(15,23,42,0.22)',
              '&:before': {
                content: '""',
                position: 'absolute',
                width: 560,
                height: 560,
                right: -150,
                top: -220,
                borderRadius: '50%',
                background: `radial-gradient(circle, ${alpha(colors.accent, 0.46)} 0%, ${alpha(theme.palette.success.main, 0.16)} 42%, transparent 70%)`,
              },
            }}
          >
            <Stack
              direction={{ xs: 'column', xl: 'row' }}
              spacing={3}
              justifyContent="space-between"
              alignItems="stretch"
              sx={{ position: 'relative', zIndex: 1 }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
                  <Avatar
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: '16px',
                      bgcolor: 'rgba(255,255,255,0.12)',
                      color: 'common.white',
                      border: '1px solid rgba(255,255,255,0.16)',
                    }}
                  >
                    <AdminPanelSettingsRoundedIcon fontSize="small" />
                  </Avatar>

                  <Typography
                    variant="overline"
                    sx={{
                      fontSize: 12,
                      fontWeight: 900,
                      letterSpacing: '0.18em',
                      color: 'rgba(255,255,255,0.66)',
                    }}
                  >
                    Informação do Parceiro
                  </Typography>
                </Stack>

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                >
                  <Avatar
                    sx={{
                      width: { xs: 72, md: 86 },
                      height: { xs: 72, md: 86 },
                      borderRadius: { xs: '26px', md: '30px' },
                      fontSize: { xs: 26, md: 32 },
                      fontWeight: 950,
                      bgcolor: 'rgba(255,255,255,0.12)',
                      color: '#fff',
                      border: '1px solid rgba(255,255,255,0.16)',
                      boxShadow: '0 18px 42px rgba(0,0,0,0.22)',
                    }}
                  >
                    {avatarLabel?.trim() ? avatarLabel : <BusinessIcon />}
                  </Avatar>

                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography
                        sx={{
                          fontSize: { xs: '2.15rem', sm: '2.85rem', md: '3.7rem' },
                          fontWeight: 950,
                          lineHeight: 0.98,
                          letterSpacing: '-0.07em',
                          color: '#ffffff',
                        }}
                      >
                        {safe(empresa.nomeEmpresa)}
                      </Typography>

                      <Chip
                        label="Parceiro"
                        sx={{
                          borderRadius: '999px',
                          fontWeight: 950,
                          color: '#fff',
                          bgcolor: 'rgba(255,255,255,0.14)',
                          border: '1px solid rgba(255,255,255,0.18)',
                        }}
                      />
                    </Stack>

                    <Typography
                      sx={{
                        mt: 1.4,
                        maxWidth: 820,
                        color: 'rgba(255,255,255,0.68)',
                        fontSize: { xs: '0.98rem', md: '1.08rem' },
                        lineHeight: 1.75,
                      }}
                    >
                      Consulta os dados principais, contactos e acessos rápidos para clientes e compras deste parceiro.
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Grid container spacing={1.25} sx={{ width: { xs: '100%', xl: 410 }, flexShrink: 0 }}>
                <Grid item xs={12} sm={6} xl={12}>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 1.6,
                      py: 1.25,
                      height: '100%',
                      borderRadius: '26px',
                      bgcolor: alpha(theme.palette.background.paper, isDark ? 0.14 : 0.74),
                      border: '1px solid rgba(255,255,255,0.18)',
                    }}
                  >
                    <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.66)', fontWeight: 800 }}>
                      Clientes
                    </Typography>

                    <Typography sx={{ fontSize: '1.35rem', fontWeight: 950, color: '#ffffff' }}>
                      {stats.clients}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} sm={6} xl={12}>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 1.6,
                      py: 1.25,
                      height: '100%',
                      borderRadius: '26px',
                      bgcolor: alpha(theme.palette.background.paper, isDark ? 0.14 : 0.74),
                      border: '1px solid rgba(255,255,255,0.18)',
                    }}
                  >
                    <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.66)', fontWeight: 800 }}>
                      Compras
                    </Typography>

                    <Typography sx={{ fontSize: '1.35rem', fontWeight: 950, color: '#ffffff' }}>
                      {stats.purchases}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              mb: 3,
              p: 1.25,
              borderRadius: '28px',
              border: `1px solid ${colors.border}`,
              bgcolor: colors.card,
              boxShadow: isDark
                ? '0 18px 42px rgba(0,0,0,0.20)'
                : '0 16px 38px rgba(15,23,42,0.06)',
            }}
          >
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                component={RouterLink}
                to="/admin/parceiros"
                startIcon={<ArrowBackIcon />}
                size="small"
                sx={{
                  height: 40,
                  borderRadius: '999px',
                  px: 1.8,
                  textTransform: 'none',
                  fontWeight: 900,
                  bgcolor: colors.cardStrong,
                  color: colors.text,
                  border: `1px solid ${colors.border}`,
                }}
              >
                Voltar à lista
              </Button>

              <Button
                component={RouterLink}
                to={`/admin/empresa/${empresaId}/clientes`}
                startIcon={<PeopleIcon />}
                size="small"
                variant="contained"
                sx={{
                  height: 40,
                  borderRadius: '999px',
                  px: 1.8,
                  textTransform: 'none',
                  fontWeight: 900,
                  boxShadow: '0 14px 28px rgba(37,99,235,0.22)',
                }}
              >
                Clientes
              </Button>

              <Button
                component={RouterLink}
                to={`/admin/empresa/${empresaId}/compras`}
                startIcon={<ShoppingCartIcon />}
                size="small"
                variant="outlined"
                sx={{
                  height: 40,
                  borderRadius: '999px',
                  px: 1.8,
                  textTransform: 'none',
                  fontWeight: 900,
                  bgcolor: colors.cardStrong,
                  borderColor: colors.border,
                }}
              >
                Compras
              </Button>
            </Stack>
          </Paper>

          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  height: '100%',
                  p: { xs: 2.25, md: 3 },
                  borderRadius: '30px',
                  border: `1px solid ${colors.border}`,
                  bgcolor: colors.card,
                  boxShadow: isDark
                    ? '0 24px 64px rgba(0,0,0,0.28)'
                    : '0 24px 60px rgba(15,23,42,0.07)',
                }}
              >
                <Stack direction="row" spacing={1.45} alignItems="center" sx={{ mb: 2.5 }}>
                  <Avatar
                    sx={{
                      width: 46,
                      height: 46,
                      borderRadius: '17px',
                      bgcolor: alpha(colors.accent, isDark ? 0.18 : 0.1),
                      color: colors.accent,
                    }}
                  >
                    <StorefrontRoundedIcon />
                  </Avatar>

                  <Box>
                    <Typography variant="h6" fontWeight={950} sx={{ color: colors.text }}>
                      Dados da Empresa
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      Informação principal e contactos do parceiro.
                    </Typography>
                  </Box>
                </Stack>

                <Grid container spacing={1.7}>
                  <Grid item xs={12} sm={6}>
                    <InfoItem icon={<BadgeRoundedIcon />} label="NIF" value={empresa.nif} />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <InfoItem icon={<EmailRoundedIcon />} label="Email" value={empresa.email} />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <InfoItem icon={<PhoneRoundedIcon />} label="Contacto" value={empresa.contacto} />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <InfoItem icon={<BusinessIcon />} label="Parceiro ID" value={empresaId} />
                  </Grid>

                  <Grid item xs={12}>
                    <InfoItem icon={<LocationOnRoundedIcon />} label="Morada" value={empresa.morada} />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>

          <Grid container spacing={2.5} sx={{ mt: 0.2 }}>
            <Grid item xs={12} md={6}>
              <KpiCard
                to={`/admin/empresa/${empresaId}/clientes`}
                icon={<PeopleIcon />}
                title="Clientes"
                value={stats.clients}
                description="Total de clientes associados a este parceiro."
                color={colors.accent}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <KpiCard
                to={`/admin/empresa/${empresaId}/compras`}
                icon={<ShoppingCartIcon />}
                title="Compras"
                value={stats.purchases}
                description="Número total de compras registadas para este parceiro."
                color={theme.palette.success.main}
              />
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </Box>
  );
}